import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Filter, Trash2, Edit3, Calendar as CalendarIcon, User, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COLUMNS = [
    { key: "todo", label: "Yapılacak", dot: "bg-muted-foreground/70" },
    { key: "in_progress", label: "Devam Ediyor", dot: "bg-[hsl(var(--warning))]" },
    { key: "review", label: "İnceleme", dot: "bg-[hsl(var(--info))]" },
    { key: "done", label: "Tamamlandı", dot: "bg-[hsl(var(--success))]" },
];
const PRIORITY_TR = { low: "Düşük", medium: "Orta", high: "Yüksek", urgent: "Acil" };
const PRIORITY_STYLE = {
    low: "bg-muted text-muted-foreground",
    medium: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
    high: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    urgent: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
};
const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "—"; } catch { return iso; } };

function TaskModal({ open, onClose, onSaved, initial, projects, users, canAssign }) {
    const isEdit = Boolean(initial?.id);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        title: "", description: "", project_id: "", status: "todo",
        priority: "medium", assignee_id: "", due_date: "",
    });
    useEffect(() => {
        if (!open) return;
        if (isEdit) setForm({
            title: initial.title || "", description: initial.description || "",
            project_id: initial.project_id || "", status: initial.status || "todo",
            priority: initial.priority || "medium", assignee_id: initial.assignee_id || "",
            due_date: initial.due_date || "",
        });
        else setForm({
            title: "", description: "", project_id: initial?.project_id || "",
            status: initial?.status || "todo", priority: "medium", assignee_id: "", due_date: "",
        });
    }, [open, initial, isEdit]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return toast.error("Başlık zorunlu.");
        setSaving(true);
        try {
            const payload = { ...form };
            Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
            const { data } = isEdit ? await api.put(`/tasks/${initial.id}`, payload) : await api.post("/tasks", payload);
            toast.success(isEdit ? "Görev güncellendi." : "Görev oluşturuldu.");
            onSaved && onSaved(data); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi."); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl">
                <DialogHeader><DialogTitle>{isEdit ? "Görevi Düzenle" : "Yeni Görev"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <input required placeholder="Görev başlığı" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
                    <textarea rows={3} placeholder="Açıklama" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <div className="grid grid-cols-2 gap-2">
                        <select className={inputCls} value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                            <option value="">— Proje —</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <select className={inputCls} value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} disabled={!canAssign} data-testid="task-assignee-select">
                            <option value="">— Atanan —</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                        <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                            {Object.entries(PRIORITY_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <input type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                        <button type="submit" disabled={saving} className="btn-brand" data-testid="task-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isEdit ? "Güncelle" : "Kaydet"}</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function TasksKanbanPage() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canAssign, setCanAssign] = useState(false);
    const [projectId, setProjectId] = useState("");
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalInitial, setModalInitial] = useState(null);
    const [dragOver, setDragOver] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page_size: 500 };
            if (projectId) params.project_id = projectId;
            const { data } = await api.get("/tasks", { params });
            setTasks(data.items || []); setCanAssign(!!data.can_assign);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [projectId]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (async () => {
            try {
                const [p, u] = await Promise.all([
                    api.get("/projects", { params: { page_size: 200 } }),
                    api.get("/employees", { params: { page_size: 200 } }).catch(() => ({ data: { items: [] } })),
                ]);
                setProjects((p.data.items || []).map((x) => ({ id: x.id, name: x.name })));
                setUsers((u.data.items || []).filter((x) => x.role !== "client").map((x) => ({ id: x.id, full_name: x.full_name })));
            } catch (err) { console.debug("silent:", err); }
        })();
    }, []);

    const columns = useMemo(() => {
        const map = { todo: [], in_progress: [], review: [], done: [] };
        for (const t of tasks) (map[t.status] || map.todo).push(t);
        return map;
    }, [tasks]);

    const changeStatus = async (id, newStatus) => {
        const orig = tasks.find(t => t.id === id);
        if (!orig || orig.status === newStatus) return;
        // optimistic
        setTasks((prev) => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));
        try { await api.patch(`/tasks/${id}/status`, { status: newStatus }); }
        catch (err) {
            toast.error(err?.response?.data?.detail || "Taşınamadı.");
            setTasks((prev) => prev.map(t => t.id === id ? { ...t, status: orig.status } : t));
        }
    };
    const del = async (id) => {
        if (!window.confirm("Bu görev silinsin mi?")) return;
        try { await api.delete(`/tasks/${id}`); setTasks((prev) => prev.filter(t => t.id !== id)); toast.success("Silindi."); }
        catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi."); }
    };

    const onDragStart = (e, id) => e.dataTransfer.setData("task_id", id);
    const onDragOver = (e, col) => { e.preventDefault(); setDragOver(col); };
    const onDrop = (e, col) => {
        e.preventDefault(); setDragOver(null);
        const id = e.dataTransfer.getData("task_id");
        if (id) changeStatus(id, col);
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight" data-testid="tasks-title">Görevler</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">Kanban görünümünde tüm görevler. Sürükle-bırak ile durum değiştirin.</p>
                </div>
                <button onClick={() => { setModalInitial(null); setModalOpen(true); }} className="btn-brand h-11" data-testid="btn-new-task"><Plus className="h-4 w-4" /> Yeni Görev</button>
            </div>

            <div className="card-elevated p-3 flex flex-wrap gap-2 items-center">
                <div className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground px-1"><Filter className="h-3.5 w-3.5" /> Filtre</div>
                <select className={`${inputCls} max-w-[300px]`} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                    <option value="">Tüm Projeler</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div className="ml-auto text-[12px] text-muted-foreground">{loading ? "Yükleniyor…" : `${tasks.length} görev`}</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {COLUMNS.map((col) => (
                    <div key={col.key}
                        onDragOver={(e) => onDragOver(e, col.key)}
                        onDragLeave={() => setDragOver(null)}
                        onDrop={(e) => onDrop(e, col.key)}
                        className={`card-elevated p-3 min-h-[400px] ${dragOver === col.key ? "ring-2 ring-inset ring-[hsl(var(--brand))]/50" : ""}`}
                        data-testid={`kanban-col-${col.key}`}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <span className={`inline-block h-2 w-2 rounded-full ${col.dot}`}></span>
                                <span className="text-[13px] font-bold">{col.label}</span>
                            </div>
                            <span className="text-[11px] font-semibold text-muted-foreground rounded-md bg-muted px-1.5 py-0.5">{columns[col.key].length}</span>
                        </div>
                        <div className="space-y-2">
                            {columns[col.key].map((t) => (
                                <div key={t.id} draggable onDragStart={(e) => onDragStart(e, t.id)}
                                     className="rounded-xl border border-border bg-background p-3 hover:shadow-sm cursor-move" data-testid={`task-card-${t.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[13px] font-bold truncate">{t.title}</div>
                                            {t.project_name && <div className="text-[11px] text-muted-foreground truncate">{t.project_name}</div>}
                                            {t.description && <div className="text-[11.5px] text-muted-foreground mt-1 line-clamp-2">{t.description}</div>}
                                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                                <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_STYLE[t.priority]}`}><Flag className="h-2.5 w-2.5" /> {PRIORITY_TR[t.priority]}</span>
                                                {t.assignee_name && <span className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground"><User className="h-2.5 w-2.5" /> {t.assignee_name}</span>}
                                                {t.due_date && <span className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground"><CalendarIcon className="h-2.5 w-2.5" /> {fmtDate(t.due_date)}</span>}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <button onClick={() => { setModalInitial(t); setModalOpen(true); }} className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--brand))]"><Edit3 className="h-3 w-3" /></button>
                                            <button onClick={() => del(t.id)} className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3 w-3" /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {columns[col.key].length === 0 && <div className="text-[11.5px] text-muted-foreground py-6 text-center">Bu sütunda görev yok.</div>}
                        </div>
                    </div>
                ))}
            </div>

            <TaskModal open={modalOpen} onClose={() => setModalOpen(false)} initial={modalInitial} projects={projects} users={users} canAssign={canAssign}
                onSaved={(x) => setTasks((prev) => { const i = prev.findIndex(y => y.id === x.id); if (i >= 0) { const c = [...prev]; c[i] = x; return c; } return [x, ...prev]; })} />
        </div>
    );
}
